import { useState, useEffect, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Search, Plus } from 'lucide-react';
import { useFavorites } from '@/lib/hooks/use-favorites';
import { useContentList } from '@/lib/hooks/use-content';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProfile } from '@/lib/providers';
import { AdminRole, Content, ContentWithConfig } from '@/types/custom';
import AudioCard from '@/components/audio-card';
import MeditationEdit from '@/components/meditation/meditation-edit';
import Loading from '@/components/loading';
import { Button } from '@/components/ui/button';
import { getDeepLink } from '@/lib/constants';
import { searchContentList } from '@/lib/search';
import EditableDescription from '@/components/editable-description';

/**
 * List meditations and visualization journeys.
 * Supports some basic search functionality.
 */
export default function MeditationCatalog() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [searchTerm, setSearchTerm] = useState(queryParams.get('query') || '');
  const [isUserTyping, setIsUserTyping] = useState(false);
  const { favorites, toggleFavorite } = useFavorites();
  const [refreshCount, setRefreshCount] = useState<number>(0); // trigger reload of catalog

  // Listen for URL changes and update search term (e.g. from navigate links)
  useEffect(() => {
    if (isUserTyping) return; // avoid overriding
    const newQueryParams = new URLSearchParams(location.search);
    const newQuery = newQueryParams.get('query') || '';
    if (newQuery !== searchTerm) {
      setSearchTerm(newQuery);
    }
  }, [location.search, searchTerm, isUserTyping]);

  const { contentWithConfigs: minutjes, loading: minutjesLoading } =
    useContentList('minutjes', [refreshCount]);
  const [filteredMinutjes, setFilteredMinutjes] =
    useState<ContentWithConfig[]>(minutjes);

  const { contentWithConfigs: meditations, loading: meditationsLoading } =
    useContentList('meditation', [refreshCount]);
  const [filteredMeditations, setFilteredMeditations] =
    useState<ContentWithConfig[]>(meditations);

  const { contentWithConfigs: journeys, loading: journeysLoading } =
    useContentList('journey', [refreshCount]);
  const [filteredJourneyMeditations, setFilteredJourneyMeditations] =
    useState<ContentWithConfig[]>(journeys);


  const navigate = useNavigate();
  const { profile, loadingProfile } = useProfile();
  const isAdmin = profile?.role === AdminRole;

  const [editingContentId, setEditingContentId] = useState('');
  const [creatingNewContent, setCreatingNewContent] = useState(false);

  // Filter meditations based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredMinutjes(minutjes);
      setFilteredMeditations(meditations);
      setFilteredJourneyMeditations(journeys);
      return;
    }

    let results = searchContentList(searchTerm, minutjes);
    setFilteredMinutjes(minutjes.filter((_, i) => results[i]));

    results = searchContentList(searchTerm, meditations);
    setFilteredMeditations(meditations.filter((_, i) => results[i]));

    results = searchContentList(searchTerm, journeys);
    setFilteredJourneyMeditations(journeys.filter((_, i) => results[i]));
  }, [searchTerm, meditations, journeys, minutjes]);

  if (minutjesLoading || meditationsLoading || journeysLoading) {
    return <Loading />;
  }

  // on content edit or creation
  const handleContentChange = () => {
    setEditingContentId('');
    setRefreshCount((prev) => prev + 1);
    setCreatingNewContent(false);
  };

  // meditation being edited (if any)
  let editingContent: Content | undefined = undefined;
  if (editingContentId) {
    let foundIdx = meditations.findIndex(
      (x) => x.content.id == editingContentId,
    );

    if (foundIdx > -1) {
      editingContent = meditations[foundIdx].content;
    } else {
      foundIdx = journeys.findIndex((x) => x.content.id == editingContentId);
      if (foundIdx > -1) {
        editingContent = journeys[foundIdx].content;
      } else {
        foundIdx = minutjes.findIndex((x) => x.content.id == editingContentId);

        if (foundIdx > -1) {
          editingContent = minutjes[foundIdx].content;
        }
      }
    }
  }

  const searchResultCount =
    filteredMeditations.length +
    filteredJourneyMeditations.length +
    filteredMinutjes.length;
  const searchMode = searchTerm.trim() !== '';

  return (
    <div className="min-h-screen p-8 space-y-8">
      <div className="space-y-2">
        <h2 className="text-2xl font-semibold">Meditaties</h2>
        <EditableDescription
          pageKey="meditaties"
          defaultText="Ontdek verschillende meditaties en oefeningen om tot rust te komen, balans te vinden en meer in verbinding te staan met jezelf."
        />
      </div>

      <div className="mb-8">
        <div className="max-w-xl mb-4">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-10 w-full rounded-full"
              placeholder="Zoek meditaties..."
              type="search"
              value={searchTerm}
              onChange={(e) => {
                setIsUserTyping(true);
                setSearchTerm(e.target.value);
              }}
              onBlur={() => setIsUserTyping(false)}
            />
          </div>
          {isAdmin && (
            <div className="relative w-full" style={{ marginTop: '12px' }}>
              <Button
                className="bg-green-600 hover:bg-green-700"
                onClick={() => setCreatingNewContent(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Meditatie Toevoegen
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-4">
        {searchMode && (
          <div className="mb-4">
            <p className="text-muted-foreground mt-2">
              {searchResultCount > 0
                ? `${searchResultCount} resultaten gevonden voor "${searchTerm}"`
                : `Geen resultaten gevonden voor "${searchTerm}"`}
            </p>
          </div>
        )}

        {filteredMinutjes.length > 0 && (
          <>
            <div className="space-y-2">
              <h3 className="text-xl text-muted-foreground font-bold">
                Minu-tjes
              </h3>
              <EditableDescription
                pageKey="meditaties-minutjes"
                defaultText="Een korte vorm van meditatie die je in een paar minuten helpt om bewust in het moment te komen en toe te bewegen naar waar jij NU behoefte aan hebt."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredMinutjes.map((meditation) => (
                <AudioCard
                  key={meditation.content.id}
                  content={meditation.content}
                  isFavorite={favorites[meditation.content.id]}
                  onFavoriteToggle={() => toggleFavorite(meditation.content.id)}
                  onClick={() =>
                    navigate(getDeepLink('meditation', meditation.content.id))
                  }
                  requestEdit={() => setEditingContentId(meditation.content.id)}
                />
              ))}
            </div>
          </>
        )}

        {filteredMeditations.length > 0 && (
          <>
            <div className="space-y-2">
              <h3 className="text-xl text-muted-foreground font-bold">
                Korte Meditaties
              </h3>
              <EditableDescription
                pageKey="meditaties-korte"
                defaultText="Kom binnen een paar minuten weer in balans binnen een thema dat nu in jou speelt!"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredMeditations.map((meditation) => (
                <AudioCard
                  key={meditation.content.id}
                  content={meditation.content}
                  isFavorite={favorites[meditation.content.id]}
                  onFavoriteToggle={() => toggleFavorite(meditation.content.id)}
                  onClick={() =>
                    navigate(getDeepLink('meditation', meditation.content.id))
                  }
                  requestEdit={() => setEditingContentId(meditation.content.id)}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {filteredJourneyMeditations.length > 0 && (
        <>
          <div className="space-y-2 mt-12">
            <h3 className="text-xl text-muted-foreground font-bold">
              Visualisatie Reizen
            </h3>
            <EditableDescription
              pageKey="meditaties-reizen"
              defaultText="Laat je mee op reis nemen in je verbeelding en onderbewustzijn en ontdek mooie en verhelderende inzichten."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredJourneyMeditations.map((meditation) => (
              <AudioCard
                key={meditation.content.id}
                content={meditation.content}
                isFavorite={favorites[meditation.content.id]}
                onFavoriteToggle={() => toggleFavorite(meditation.content.id)}
                onClick={() =>
                  navigate(getDeepLink('meditation', meditation.content.id))
                }
                requestEdit={() => setEditingContentId(meditation.content.id)}
              />
            ))}
          </div>
        </>
      )}



      {isAdmin && editingContent && (
        <MeditationEdit
          content={editingContent}
          onSave={() => handleContentChange()}
          isOpen={Boolean(editingContentId)}
          handleClose={() => setEditingContentId('')}
        />
      )}

      {isAdmin && creatingNewContent && (
        <MeditationEdit
          onSave={() => handleContentChange()}
          isOpen={creatingNewContent}
          handleClose={() => setCreatingNewContent(false)}
        />
      )}
    </div>
  );
}
